import type { z } from 'zod'

/**
 * Flattens a zod safeParse failure into `{ 'dotted.path': message }` with
 * short client-facing copy — the credit-application wizard and the public
 * reference form both key their FieldErrors off it. Custom issues (the
 * paper-form refinements) carry their own message through unchanged; the
 * first issue per path wins.
 */
export type FieldErrors = Record<string, string>

function friendlyMessage(issue: z.core.$ZodIssue): string {
  switch (issue.code) {
    case 'custom':
      return issue.message
    case 'invalid_format':
      return issue.format === 'email' ? 'Enter a valid email address.' : 'Enter a valid date.'
    case 'too_big':
      return 'Too long — shorten this.'
    default:
      // invalid_type / too_small / invalid_value — a blank required field.
      return 'Required.'
  }
}

export function zodFieldErrors(
  result: z.ZodSafeParseResult<unknown>,
  prefix?: string,
): FieldErrors {
  if (result.success) return {}
  const errors: FieldErrors = {}
  for (const issue of result.error.issues) {
    const key = [prefix, ...issue.path].filter((p) => p !== undefined).join('.')
    if (!(key in errors)) errors[key] = friendlyMessage(issue)
  }
  return errors
}
